import { FlagOutlined, PlusCircleOutlined, SaveOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Checkbox, Col, Form, Input, InputNumber, message, Modal, Row, Select, Spin } from 'antd';
import React, { useState } from 'react';
import Helmet from 'react-helmet';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import MainContent from '@/layouts/MainContent';
import { Trans, useTranslation } from 'react-i18next';
import { ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { v4 as uuidv4 } from 'uuid';
import { RouteType } from '@/constants';
import StationInput from '@/components/FormItems/StationInput';
import CountryInput from '@/components/FormItems/CountryInput';
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { sortableKeyboardCoordinates, SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import SortableStopItem from './SortableStopItem';
import StopDetailModal from './StopDetailModal';
import { clearAllRoutesCache } from '@/hooks/data/masterDataHook';
import { RouteDto, CompanyTicketClassDto } from '@/__generated';
import { apiSdk } from '@/httpClient';
import { useCanManageFleetTrip } from '@/hooks/useHasPermissions';
const PAGE_TITLE = 'routesPage_Title';

const filters: Array<IFilterItem> = [
  { type: 'TEXT', name: 'searchText', label: 'routesPage_SearchText' },
  { type: 'LIST', name: 'routeType', label: 'routesPage_RouteType', listData: RouteType.__getSelectOptions() },
  { type: 'SELECT', name: 'stationId', label: 'routesPage_Station', customElm: <StationInput placeholder={<Trans>routesPage_Station</Trans>} /> },
];

const RoutesPage = () => {
  const [t] = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stopItems, setStopItems] = React.useState<{ id: string; stationId?: string; distanceFromSource?: number }[]>([]);
  const [route, setRoute] = React.useState<RouteDto>();
  const canUpdate = useCanManageFleetTrip();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const clearCache = () => {
    clearAllRoutesCache();
  };
  const createEntity = values => {
    setLoading(true);
    apiSdk.RouteService.create({
      body: {
        ...values,
        routeStops:
          stopItems.map(item => {
            return {
              distanceFromSource: values[`routeStops[${item.id}].distanceFromSource`],
              stationId: values[`routeStops[${item.id}].stationId`],
            };
          }) || [],
      },
    })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          clearCache();
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateEntity = values => {
    setLoading(true);
    apiSdk.RouteService.update({
      body: {
        ...values,
        routeStops:
          stopItems.map(item => {
            return {
              distanceFromSource: values[`routeStops[${item.id}].distanceFromSource`],
              stationId: values[`routeStops[${item.id}].stationId`],
            };
          }) || [],
      },
    })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          clearCache();
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onEdit = (id: string) => {
    setIsNewForm(false);
    setFormVisible(true);
    setLoading(true);
    apiSdk.RouteService.getEntityForUpdate({ id: id })
      .then(res => {
        let stops =
          res.routeStops?.map(stop => {
            return {
              id: uuidv4(),
              distanceFromSource: stop.distanceFromSource,
              stationId: stop.stationId,
            };
          }) || [];
        setStopItems(stops);
        let stopFormValues = {};
        stops.forEach(stop => {
          stopFormValues[`routeStops[${stop.id}].stationId`] = stop.stationId;
          stopFormValues[`routeStops[${stop.id}].distanceFromSource`] = stop.distanceFromSource;
        });
        form.setFieldsValue({
          ...res,
          ...stopFormValues,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const columns: IColumnType<RouteDto>[] = React.useMemo(() => {
    return [
      {
        title: 'routesPage_RouteName',
        dataIndex: 'routeName',
        ellipsis: true,
        sorter: true,
        defaultSortOrder: 'ascend',
      },
      {
        title: 'routesPage_RouteInternalCode',
        dataIndex: 'routeInternalCode',
        sorter: true,
        ellipsis: true,
      },
      {
        title: 'routesPage_RouteType',
        dataIndex: 'routeType',
        sorter: true,
        ellipsis: true,
        render: v => RouteType.__getValue(v),
      },
      {
        title: 'routesPage_Origin',
        dataIndex: 'originStation',
        ellipsis: true,
      },
      {
        title: 'routesPage_Destination',
        dataIndex: 'destinationStation',
        ellipsis: true,
      },
      {
        title: 'routesPage_Distance',
        dataIndex: 'distance',
        sorter: true,
        ellipsis: true,
        width: 130,
        render: v => `${v} Kms`,
      },
      {
        title: 'routesPage_Stops',
        dataIndex: 'noOfStops',
        ellipsis: true,
        width: 100,
        align: 'center',
        render: (v, record) => (
          <Button
            type="link"
            size="small"
            onClick={() => {
              setRoute(record);
            }}
          >
            <b>{v}</b>
          </Button>
        ),
      },
    ] as IColumnType<CompanyTicketClassDto>[];
  }, []);
  const sourceCountryId = Form.useWatch('sourceCountryId', form);
  const destinationCountryId = Form.useWatch('destinationCountryId', form);

  function handleDragEnd(event: any) {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = stopItems.findIndex(x => x.id === active.id);
      const newIndex = stopItems.findIndex(x => x.id === over.id);
      setStopItems(arrayMove(stopItems, oldIndex, newIndex));
    }
  }

  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={
        canUpdate
          ? [
              <Button
                key="create"
                type="primary"
                className="nta-create-btn"
                icon={<PlusCircleOutlined />}
                onClick={() => {
                  form.resetFields();
                  setIsNewForm(true);
                  setFormVisible(true);
                }}
              >
                {t('createNew')}
              </Button>,
            ]
          : null
      }
      icon={<FlagOutlined style={{ fontSize: 16 }} />}
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/fleets/routes/get-routes"
          bordered
          order_by="createdOn"
          order_by_asc={true}
          allowEdit={canUpdate}
          onEdit={record => {
            onEdit(record.id);
          }}
          allowDelete={canUpdate}
          delete_url="/fleets/routes/delete"
          filters={filters}
          onDeleteCompleted={clearCache}
        ></ZTable>
        {formVisible ? (
          <Modal
            open={formVisible}
            maskClosable={false}
            width={ModalWidth.large}
            className="nta-custom-form"
            title={isNewForm ? t('routesPage_CreateRoute') : t('routesPage_UpdateRoute')}
            forceRender={true}
            okText={t('save')}
            cancelText={t('cancel')}
            okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
            onCancel={() => {
              form.resetFields();
              setFormVisible(false);
            }}
            onOk={() => {
              form
                .validateFields()
                .then(values => {
                  if (values.id) {
                    updateEntity(values);
                  } else {
                    createEntity(values);
                  }
                })
                .catch(info => {});
            }}
          >
            <Spin spinning={loading}>
              <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10 }} initialValues={{ routeStops: [] }}>
                <Form.Item name="id" className="hidden">
                  <Input type="hidden" />
                </Form.Item>

                <Row gutter={ROW_GUTTER}>
                  <Col md={12}>
                    <Form.Item label={t('routesPage_RouteName')} name="routeName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Input />
                    </Form.Item>
                    <Form.Item
                      label={t('routesPage_RouteInternalCode')}
                      name="routeInternalCode"
                      rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item label={t('routesPage_RouteType')} name="routeType" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Select options={RouteType.__getSelectOptions()} />
                    </Form.Item>
                    <Form.Item label={t('routesPage_Distance')} name="distance" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <InputNumber min={0} addonAfter={t('(Kms)')} className="w-100" />
                    </Form.Item>
                    {isNewForm ? (
                      <Form.Item label="   " colon={false} name="isRoundTrip" valuePropName="checked">
                        <Checkbox>{t('routesPage_IsRoundTrip')} </Checkbox>
                      </Form.Item>
                    ) : null}
                  </Col>
                  <Col md={12}>
                    <Form.Item label={t('routesPage_OriginCountry')} name="sourceCountryId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <CountryInput
                        onChange={() => {
                          form.setFieldValue('sourceStationId', null);
                        }}
                      />
                    </Form.Item>

                    <Form.Item label={t('routesPage_OriginStation')} name="sourceStationId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <StationInput countryIds={sourceCountryId ? [sourceCountryId] : undefined} disabled={!sourceCountryId} />
                    </Form.Item>
                    <Form.Item
                      label={t('routesPage_DestinationCountry')}
                      name="destinationCountryId"
                      rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                    >
                      <CountryInput
                        onChange={() => {
                          form.setFieldValue('destinationStationId', null);
                        }}
                      />
                    </Form.Item>

                    <Form.Item
                      label={t('routesPage_DestinationStation')}
                      name="destinationStationId"
                      rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                    >
                      <StationInput countryIds={destinationCountryId ? [destinationCountryId] : undefined} disabled={!destinationCountryId} />
                    </Form.Item>
                  </Col>
                </Row>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <Card
                    className="mtopo-card"
                    title={t('routesPage_ManageStops')}
                    size="small"
                    extra={
                      <Button
                        icon={<PlusOutlined />}
                        size="middle"
                        type="primary"
                        onClick={() => {
                          setStopItems(items => [...items, { id: uuidv4() }]);
                        }}
                      >
                        {t('routesPage_AddStop')}
                      </Button>
                    }
                  >
                    <SortableContext items={stopItems} strategy={verticalListSortingStrategy}>
                      {stopItems.map(stop => {
                        return (
                          <SortableStopItem
                            destinationCountryId={destinationCountryId}
                            sourceCountryId={sourceCountryId}
                            stopItem={stop}
                            key={stop.id}
                            remove={id => {
                              setStopItems(x => x.filter(x => x.id !== id));
                            }}
                          />
                        );
                      })}
                    </SortableContext>
                  </Card>
                </DndContext>
              </Form>
            </Spin>
          </Modal>
        ) : null}
      </div>
      {route ? (
        <StopDetailModal
          route={route}
          onClose={() => {
            setRoute(undefined);
          }}
        />
      ) : null}
    </MainContent>
  );
};
export default RoutesPage;
